import { useEffect, useState } from 'react';
import { Box, Paper, Stack, Typography, Chip, Tooltip, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDistance, differenceInMilliseconds } from 'date-fns';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DataGridTable from '@/components/datagridTable/datagridTable';
import { GridColDef } from '@mui/x-data-grid';
import { useApiContext } from '@/contexts/apiContext';
import ProgressIndicator from '../progressIndicator';
import ErrorMessage from '../errorMessage';

type Props = { dataSourceInstance: any; maxHeight: number };

const RunHistory = ({ dataSourceInstance = {}, maxHeight }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();
  const [runs, setRuns] = useState<any[]>([]);

  const dataTimeFormat = import.meta.env.VITE_DATA_TIME_FORMAT1 || 'yyyy-MM-dd HH:mm:ss';

  const fetchRunHistory = async () => {
    if (!dataSourceInstance?.id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await apiContext.getDataSourceInstanceProcessingStatuses(
        dataSourceInstance.dataSourceDefId,
        dataSourceInstance.id,
      );
      setRuns(data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRunHistory();
  }, [dataSourceInstance?.id]);

  const columns: GridColDef[] = [
    {
      field: 'startTime',
      headerName: 'Start Time',
      minWidth: 180,
      flex: 1,
      valueGetter: (value: any) => (value ? formatDate(value, dataTimeFormat) : ''),
    },
    {
      field: 'endTime',
      headerName: 'End Time',
      minWidth: 180,
      flex: 1,
      valueGetter: (value: any) => (value ? formatDate(value, dataTimeFormat) : '--'),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 130,
      valueGetter: (value: any, row: any) => {
        if (!row.startTime) return '--';
        const end = row.endTime ? new Date(row.endTime) : new Date();
        const diffMs = differenceInMilliseconds(end, new Date(row.startTime));
        if (diffMs < 1000) return `${diffMs}ms`;
        return formatDistance(end, new Date(row.startTime));
      },
    },
    {
      field: 'triggerType',
      headerName: 'Trigger',
      width: 120,
      renderCell: (params) => {
        const val = params.value || 'unknown';
        const isManual = val.toLowerCase().includes('manual') || val.toLowerCase().includes('demand');
        return (
          <Chip
            label={isManual ? 'On-Demand' : 'Scheduled'}
            size="small"
            color={isManual ? 'secondary' : 'default'}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'successItemCount',
      headerName: 'Succeeded',
      width: 110,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: params.value > 0 ? 'success.main' : 'text.secondary' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'failedItemCount',
      headerName: 'Failed',
      width: 100,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: params.value > 0 ? 'error.main' : 'text.secondary' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      valueGetter: (value: any, row: any) => {
        if (row.error) return 'Failed';
        if (!row.endTime) return 'Running';
        return row.failedItemCount > 0 ? 'Completed with Errors' : 'Success';
      },
      renderCell: (params) => {
        const status = params.value;
        let color: 'success' | 'warning' | 'info' | 'error' | 'default' = 'default';
        let icon = null;

        if (status === 'Running') {
          color = 'info';
          icon = <PlayCircleOutlineIcon sx={{ fontSize: 14 }} />;
        } else if (status === 'Success') {
          color = 'success';
          icon = <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />;
        } else if (status === 'Failed') {
          color = 'error';
          icon = <ErrorOutlineIcon sx={{ fontSize: 14 }} />;
        } else {
          color = 'warning';
          icon = <ErrorOutlineIcon sx={{ fontSize: 14 }} />;
        }

        return (
          <Chip
            icon={icon || undefined}
            label={status}
            size="small"
            color={color}
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'error',
      headerName: 'Error Message',
      width: 250,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <Typography variant="body2" sx={{ color: 'error.main', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {params.value || '-'}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden', p: 1 }} spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          Ingestion Run History ({runs.length} runs logged)
        </Typography>
        <Tooltip title="Refresh History">
          <IconButton onClick={fetchRunHistory} size="small" color="primary">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && <ErrorMessage error={error} />}

      <Paper sx={{ flexGrow: 1, overflow: 'hidden', borderRadius: '8px' }} variant="outlined">
        {loading && runs.length === 0 ? (
          <ProgressIndicator />
        ) : (
          <DataGridTable
            rows={runs}
            columns={columns}
            loading={loading}
            height={`${maxHeight - 50}px`}
          />
        )}
      </Paper>
    </Stack>
  );
};

export default RunHistory;
